import React from 'react';
import PropTypes from 'prop-types';
import isRequiredIf from 'react-proptype-conditional-require';
import PromoBanner from '@deliveroo/consumer-component-library/components/PromoBanner';
import ServiceBanner from '@deliveroo/consumer-component-library/components/ServiceBanner';
import withPlaceholder from '@deliveroo/consumer-component-library/components/withPlaceholder';
import BackgroundImage from '@deliveroo/consumer-component-library/components/BackgroundImage';
import PickupShowcaseBanner from '../PickupShowcaseBanner';
import ResultsSummaryError from '../ResultsSummaryError';

const BANNER_THEMES = {
  BANNER_EMPTY: 'banner-empty',
  BANNER_MARKETING_A: 'teal',
  BANNER_MARKETING_B: 'berry',
  BANNER_MARKETING_C: 'purple',
  BANNER_PICKUP_SHOWCASE: 'banner-pickup-showcase',
  BANNER_PLACEHOLDER: 'placeholder',
  BANNER_SERVER_ERROR: 'banner-server-error',
  BANNER_SERVICE_ADVISORY: 'banner-service-advisory',
};

const HomeBanner = ({
  buttonCaption,
  caption,
  heading,
  href,
  images,
  imageSize,
  isPlaceholder,
  isSearchResults,
  onClick,
  type,
}) => {
  const theme = BANNER_THEMES[type];
  const isPickupShowcase = theme === BANNER_THEMES.BANNER_PICKUP_SHOWCASE;
  const isServiceAdvisory = theme === BANNER_THEMES.BANNER_SERVICE_ADVISORY;
  const isErrorTheme = [BANNER_THEMES.BANNER_SERVER_ERROR, BANNER_THEMES.BANNER_EMPTY].includes(
    theme,
  );
  const imageUrl = images && images.image;

  if (isErrorTheme) {
    return (
      <ResultsSummaryError
        errorData={{
          type: theme === 'banner-empty' ? '' : 'server-error',
          title: heading,
          message: caption,
          buttonText: buttonCaption,
          buttonAction: onClick,
          imageUrl,
        }}
        loading={isPlaceholder}
        isSearchResults={isSearchResults}
      />
    );
  }
  if (isServiceAdvisory && isPlaceholder) {
    // There is currently a rendering issue in CCL's ServiceBanner where the
    // placeholder variant renders its content when passed in props.
    return <ServiceBanner isPlaceholder />;
  }
  if (isServiceAdvisory) {
    return (
      <ServiceBanner
        body={caption}
        imageSize={imageSize}
        onClick={onClick}
        renderImage={imageUrl ? () => <BackgroundImage src={imageUrl} /> : undefined}
        title={heading}
      />
    );
  }
  if (isPickupShowcase) {
    return (
      <PickupShowcaseBanner
        body={caption}
        cta={buttonCaption}
        imageUrl={imageUrl}
        isPlaceholder={isPlaceholder}
        onClick={onClick}
        title={heading}
      />
    );
  }
  return (
    <PromoBanner
      title={heading}
      body={caption}
      onClick={onClick}
      isPlaceholder={isPlaceholder}
      href={href}
      theme={theme !== 'placeholder' ? theme : undefined}
    />
  );
};

HomeBanner.propTypes = {
  /** Theme of the banner */
  type: isRequiredIf(PropTypes.oneOf(Object.keys(BANNER_THEMES)), (props) => !props.isPlaceholder),

  /** Heading for the banner */
  heading: isRequiredIf(PropTypes.string, (props) => !props.isPlaceholder),

  /** Caption for the banner */
  caption: isRequiredIf(PropTypes.string, (props) => !props.isPlaceholder),

  /** If provided displays a CTA button */
  buttonCaption: PropTypes.string,

  /** If provided, PromoBanner will render as a, <a> rather than a <button> */
  href: PropTypes.string,

  /** Onclick callback */
  onClick: PropTypes.func,
  isPlaceholder: PropTypes.bool,
  images: PropTypes.shape({
    image: PropTypes.string,
    icon: PropTypes.shape({
      name: PropTypes.string,
      image: PropTypes.string,
    }),
  }),
  imageSize: PropTypes.oneOf(['medium', 'large']),
  isSearchResults: PropTypes.bool,
};

HomeBanner.defaultProps = {
  buttonCaption: '',
  caption: undefined,
  heading: undefined,
  href: undefined,
  images: undefined,
  imageSize: 'medium',
  isPlaceholder: false,
  isSearchResults: false,
  onClick: undefined,
  type: undefined,
};

HomeBanner.placeholderProps = {
  type: 'BANNER_PLACEHOLDER',
  heading: '',
  caption: '',
};

export default withPlaceholder(HomeBanner);
